var _a;
const u = up.util;
up.Change.UpdateLayer = (_a = class UpdateLayer extends up.Change.Addition {
        constructor(options) {
            options = up.RenderOptions.finalize(options);
            super(options);
            this.layer = options.layer;
            this.target = options.target;
            this._context = options.context;
            this._steps = up.fragment.parseTargetSteps(this.target, this.options);
        }
        getPreflightProps() {
            this._matchPreflight();
            let fragments = this._getFragments();
            return {
                layer: this.layer,
                bindLayer: this.layer,
                mode: this.layer.mode,
                context: u.merge(this.layer.context, this._context),
                origin: this.options.origin,
                target: this._bestPreflightSelector(),
                fragments,
                bindFragments: fragments,
            };
        }
        _bestPreflightSelector() {
            this._matchPreflight();
            const isProbable = (step) => { var _b; return !step.maybe || ((_b = step.oldElement) === null || _b === void 0 ? void 0 : _b.isConnected); };
            let bestSteps = this._steps.filter(isProbable);
            let selectors = u.map(bestSteps, 'selector');
            return selectors.join(', ') || ':none';
        }
        _getFragments() {
            this._matchPreflight();
            return u.map(this._steps, 'oldElement');
        }
        execute(responseDoc, onApplicable) {
            this._matchPostflight(responseDoc);
            onApplicable();
            let renderOtherLayersOnce = u.memoize(() => this._renderOtherLayers(responseDoc));
            let unbindClosing = this.layer.on('up:layer:accepting up:layer:dismissing', renderOtherLayersOnce);
            try {
                let partialResults = [
                    ...this._renderCurrentLayer(responseDoc),
                    ...renderOtherLayersOnce(),
                ];
                return new up.RenderResult({
                    target: this.target,
                    layer: this.layer,
                    renderOptions: this.options,
                    partialResults,
                });
            }
            finally {
                unbindClosing();
            }
        }
        _renderCurrentLayer(responseDoc) {
            if (this._steps.length) {
                up.puts('up.render()', `Updating "${this._bestPreflightSelector()}" in ${this.layer}`);
            }
            this._coordinateSteps();
            if (this.options.saveScroll) {
                up.viewport.saveScroll({ layer: this.layer });
            }
            if (this.options.saveFocus) {
                up.viewport.saveFocus({ layer: this.layer });
            }
            let { peel } = this.options;
            if (peel) {
                this.layer.peel({ history: !this._hasHistory(), intent: peel });
            }
            if (this.options.abort !== false) {
                up.fragment.abort(this._getFragments(), {
                    reason: 'Fragment is being replaced',
                    jid: this.options.jid,
                });
            }
            Object.assign(this.layer.context, this._context);
            this.handleLayerChangeRequests(u.map(this._steps, 'newElement'), this._getNewLocation());
            this.layer.updateHistory(this._getEffectiveHistoryOptions());
            return this.executeSteps({
                steps: this._steps,
                noneOptions: this.options,
                responseDoc,
            });
        }
        _renderOtherLayers(responseDoc) {
            let otherLayerSteps = this._getHungrySteps().other;
            return this.executeSteps({
                steps: otherLayerSteps,
                responseDoc,
            });
        }
        _matchPreflight() {
            this._matchOldElements();
            this._compressNestedSteps();
        }
        _matchPostflight(responseDoc) {
            this._matchOldElements();
            this._addHungryStepsOnCurrentLayer();
            this._compressNestedSteps();
            this._matchNewElements(responseDoc);
        }
        _addHungryStepsOnCurrentLayer() {
            this._steps.push(...this._getHungrySteps().current);
        }
        _matchOldElements() {
            this._steps = this._steps.filter((step) => {
                const finder = new up.FragmentFinder(u.pick(step, ['selector', 'origin', 'layer', 'match', 'preferOldElements']));
                step.oldElement || (step.oldElement = finder.find());
                if (step.oldElement) {
                    return true;
                }
                else if (!step.maybe) {
                    throw new up.CannotMatch();
                }
            });
        }
        _matchNewElements(responseDoc) {
            this._steps = responseDoc.selectSteps(this._steps);
        }
        _compressNestedSteps() {
            this._steps = up.fragment.compressNestedSteps(this._steps);
        }
        _getHungrySteps() {
            return up.radio.hungrySteps(this._getEffectiveRenderOptions());
        }
        _coordinateSteps() {
            let focusCapsule = up.FocusCapsule.preserve(this.layer);
            let oldScrollTops = up.viewport.getScrollTops({ layer: this.layer });
            this._steps.forEach((step, i) => {
                step.focusCapsule = focusCapsule;
                step.oldScrollTops = oldScrollTops;
                if (step.scrollMap)
                    step.scroll = false;
                if (i > 0) {
                    step.focus = false;
                    step.scrollMap = undefined;
                    if (step.scroll !== 'keep')
                        step.scroll = false;
                    step.data = undefined;
                }
            });
        }
        _hasHistory() {
            return u.evalAutoOption(this.options.history, this._hasAutoHistory.bind(this));
        }
        _hasAutoHistory() {
            const oldFragments = u.map(this._steps, 'oldElement');
            return up.fragment.hasAutoHistory(oldFragments, this.layer);
        }
        _getNewLocation() {
            return this._getEffectiveHistoryOptions().location;
        }
        _getEffectiveRenderOptions() {
            return Object.assign(Object.assign({}, this.options), { layer: this.layer, history: this._hasHistory() });
        }
        _getEffectiveHistoryOptions() {
            if (this._hasHistory()) {
                return up.history.options(this.options);
            }
            else {
                return {};
            }
        }
    },
    (() => {
        u.memoizeMethod(_a.prototype, {
            _matchPreflight: true,
            _matchOldElements: true,
            _hasHistory: true,
            _getHungrySteps: true,
        });
    })(),
    _a);
